import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Music, Store, Play, ArrowLeft, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Countdown } from "@/components/Countdown";
import { TicketCheckout } from "@/components/TicketCheckout";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function EventDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setEvent(null);
    setNotFound(false);
    let alive = true;
    const load = (attempt = 0) =>
      api.get(`/events/${slug}`)
        .then((r) => { if (alive) setEvent(r.data); })
        .catch((e) => {
          if (e.response && e.response.status === 404) { if (alive) setNotFound(true); return; }
          if (alive && attempt < 3) setTimeout(() => load(attempt + 1), 700);
          else if (alive) setNotFound(true);
        });
    load();
    return () => { alive = false; };
  }, [slug]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 text-center">
        <h1 className="font-display text-4xl font-black uppercase text-cyan-50">Ride Not Found</h1>
        <Link to="/events" className="mt-6 inline-block font-display font-bold uppercase text-cyan-400">← Back to Events</Link>
      </div>
    );

  if (!event) return <div className="px-4 py-32 text-center text-neutral-500">Loading ride…</div>;

  const isPast = new Date(event.date).getTime() < Date.now();
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
    `${event.address} ${event.city} ${event.state} ${event.map_query}`
  )}&output=embed`;

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden bg-stone-900">
        {event.hero_image && (
          <img src={event.hero_image} alt={event.title} className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="mx-auto max-w-7xl px-4 pb-8 sm:px-6">
            <Link to="/events" className="mb-4 inline-flex items-center gap-1 font-mono-label text-xs uppercase text-cyan-400" data-testid="back-to-events">
              <ArrowLeft className="h-3.5 w-3.5" /> All Events
            </Link>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl font-display text-3xl font-black uppercase leading-[0.95] text-cyan-50 drop-shadow-lg sm:text-5xl">
              {event.title}
            </motion.h1>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-stone-200">
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-cyan-400" /> {fmtDate(event.date)}</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-cyan-400" /> {event.location_name}, {event.city} {event.state}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-12">
        {/* Left column */}
        <div className="space-y-12 lg:col-span-7">
          {!isPast && (
            <div className="rounded-2xl border border-cyan-500/20 bg-[#0a0a0a] p-6">
              <span className="eyebrow flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Countdown To Ride</span>
              <div className="mt-4"><Countdown date={event.date} /></div>
            </div>
          )}

          <div>
            <h2 className="font-display text-2xl font-extrabold uppercase text-cyan-400">The Vibe</h2>
            <p className="mt-4 whitespace-pre-line text-neutral-300">{event.description}</p>
          </div>

          {event.video_url && (
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase text-cyan-400">
                <Play className="h-5 w-5" /> Hype Video
              </h2>
              <div className="mt-4 aspect-video overflow-hidden rounded-2xl border border-neutral-800">
                <iframe src={event.video_url} title="Hype video" className="h-full w-full" allowFullScreen data-testid="hype-video" />
              </div>
            </div>
          )}

          {event.lineup?.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase text-cyan-400">
                <Music className="h-5 w-5" /> Lineup
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-2">
                {event.lineup.map((a, i) => (
                  <div key={i} className="rounded-xl border border-neutral-800 bg-[#0a0a0a] p-4">
                    <div className="font-display text-lg font-bold uppercase text-stone-100">{a.name}</div>
                    <div className="font-mono-label text-[10px] uppercase tracking-wider text-cyan-500">{a.role}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {event.vendors?.length > 0 && (
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase text-cyan-400">
                <Store className="h-5 w-5" /> Food & Vendors
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {event.vendors.map((v, i) => (
                  <span key={i} className="rounded-full border border-cyan-500/30 bg-cyan-950/40 px-4 py-2 font-mono-label text-xs uppercase tracking-wider text-cyan-300">
                    {v.name} · {v.type}
                  </span>
                ))}
              </div>
            </div>
          )}

          {event.gallery?.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-extrabold uppercase text-cyan-400">Gallery</h2>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {event.gallery.map((g, i) => (
                  <img key={i} src={g} alt={`gallery ${i}`} className="h-36 w-full rounded-xl object-cover" />
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-display text-2xl font-extrabold uppercase text-cyan-400">Location</h2>
            <p className="mt-2 text-neutral-300">{event.address}, {event.city}, {event.state}</p>
            <div className="mt-4 aspect-video overflow-hidden rounded-2xl border border-neutral-800">
              <iframe src={mapSrc} title="map" className="h-full w-full" loading="lazy" data-testid="event-map" />
            </div>
          </div>

          <div className="glass flex flex-wrap items-center justify-between gap-4 rounded-2xl p-5" data-testid="event-partner-cta">
            <div>
              <span className="eyebrow">Vendors & Sponsors</span>
              <p className="mt-1 text-sm text-neutral-300">Sell at this event or put your brand in the arena.</p>
            </div>
            <div className="flex gap-3">
              <Link to={`/vendors?event=${event.slug}`} data-testid="event-vendor-link" className="btn-chrome rounded-full px-5 py-2.5 font-cond text-sm font-bold uppercase">Reserve a Booth</Link>
              <Link to="/sponsorships" data-testid="event-sponsor-link" className="rounded-full border border-cyan-500/50 px-5 py-2.5 font-cond text-sm font-bold uppercase text-cyan-400 hover:bg-cyan-500/10">Sponsor</Link>
            </div>
          </div>
        </div>

        {/* Right sticky checkout */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <TicketCheckout event={event} />
          </div>
        </div>
      </div>
    </div>
  );
}
